import { useCallback, useEffect, useMemo, useRef } from "react";
import { CHAT_TYPES } from "@/js/constants/chat";
import { buildKoreUiConfigFromChatConfig } from "@/js/helpers/mapMonarchUiConfigToKore";
import {
  hideChatIcon,
  showChatIcon,
} from "@/hooks/chat/helpers/chatbotHelpers";
import { mergeProactiveModalContent } from "@/hooks/chat/helpers/mergeProactiveModalContent";

const MAX_SDK_RETRIES = 20;
const RETRY_MS = 500;

/**
 * After the Kore script loads, applies Monarch `position`, `proactive`, `triggerType`, `pageType`, `uiConfig`
 * via `KoreChatSDK.showChatIcon` (same idea as fuel-cart `useChatbotSDKInit`).
 *
 * The Kore bundle is loaded from `KORE_CHAT_SDK_BASE_URL/<sdkVersion>/`.
 * Match fuel-cart: if Monarch omits `proactive.message`, do not set `message` (Kore uses bundle default
 * teaser). If Monarch sends `message: ""`, forward it so Kore hides side-rail copy (icon-only teaser).
 */
export const useKoreChatIconInit = ({
  chatConfig,
  chatType,
  triggerOverrides,
}) => {
  const initializedRef = useRef(false);

  const iconConfig = useMemo(() => {
    if (!chatConfig || typeof chatConfig !== "object") return {};

    const next = {};
    const { position, triggerType, pageType } = chatConfig;
    const proactive = triggerOverrides?.proactive
      ? mergeProactiveModalContent(
          chatConfig.proactive,
          triggerOverrides.proactive,
        )
      : chatConfig.proactive;

    if (position && typeof position === "object") {
      const safePosition = {};
      if (
        typeof position.bottom === "string" &&
        position.bottom.trim() !== ""
      ) {
        safePosition.bottom = position.bottom.trim();
      }
      if (typeof position.right === "string" && position.right.trim() !== "") {
        safePosition.right = position.right.trim();
      }
      if (Object.keys(safePosition).length > 0) {
        next.position = safePosition;
      }
    }
    if (triggerType) {
      next.trigger_type = triggerType;
    }
    if (proactive && typeof proactive === "object") {
      const safeProactive = {};
      if (Object.prototype.hasOwnProperty.call(proactive, "message")) {
        const m = proactive.message;
        safeProactive.message =
          typeof m === "string" ? m : m == null ? "" : String(m);
      }
      if (proactive.type) {
        safeProactive.type = proactive.type;
      }
      if (proactive.display) {
        safeProactive.display = proactive.display;
      }

      if (Object.keys(safeProactive).length > 0) {
        next.proactive = safeProactive;
      }
    }
    if (pageType) {
      next.page_type = pageType;
    }

    const uiMerged = buildKoreUiConfigFromChatConfig(chatConfig);
    if (uiMerged && Object.keys(uiMerged).length > 0) {
      next.ui_config = uiMerged;
    }

    return next;
  }, [chatConfig, triggerOverrides]);

  const configString = useMemo(() => JSON.stringify(iconConfig), [iconConfig]);

  const isChatEnabled = chatType != null && chatType !== CHAT_TYPES.NONE;

  const showChatIconWithConfig = useCallback(() => {
    const success = showChatIcon(iconConfig);
    if (success) {
      initializedRef.current = true;
    }
    return success;
  }, [iconConfig]);

  const hideChatIconAndReset = useCallback(() => {
    const success = hideChatIcon();
    if (success) {
      initializedRef.current = false;
    }
    return success;
  }, []);

  useEffect(() => {
    if (!isChatEnabled) return;

    let retryCount = 0;
    let timeoutId = null;

    const clearRetry = () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    let scheduleRetry;

    const checkAndInit = () => {
      if (typeof window === "undefined") return;

      if (window.KoreChatSDK) {
        const shown = showChatIconWithConfig();
        if (!shown && retryCount < MAX_SDK_RETRIES) {
          retryCount += 1;
          scheduleRetry();
        }
      } else if (retryCount < MAX_SDK_RETRIES) {
        retryCount += 1;
        scheduleRetry();
      }
    };

    scheduleRetry = () => {
      timeoutId = setTimeout(checkAndInit, RETRY_MS);
    };

    checkAndInit();

    return () => {
      clearRetry();
    };
  }, [isChatEnabled, configString, showChatIconWithConfig]);

  useEffect(() => {
    if (isChatEnabled) return;
    if (initializedRef.current) {
      hideChatIconAndReset();
    }
    initializedRef.current = false;
  }, [isChatEnabled, hideChatIconAndReset]);
};
